import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, Radio, MessageSquare, Users, Link2, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { LiveStats } from "@/types/api";
import { cn } from "@/lib/utils";

const HEALTH_CONFIG: Record<
  LiveStats["systemHealth"],
  { label: string; badgeClass: string; dotClass: string }
> = {
  healthy: {
    label: "Healthy",
    badgeClass: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    dotClass: "bg-emerald-400",
  },
  warning: {
    label: "Warning",
    badgeClass: "bg-amber-500/20 text-amber-300 border-amber-400/30",
    dotClass: "bg-amber-400",
  },
  error: {
    label: "Degraded",
    badgeClass: "bg-rose-500/20 text-rose-300 border-rose-400/30",
    dotClass: "bg-rose-400",
  },
};

export function LiveStatsBar({ stats }: { stats: LiveStats | undefined }) {
  const prefersReducedMotion = useReducedMotion();
  const health = stats?.systemHealth ?? "healthy";
  const healthCfg = HEALTH_CONFIG[health] ?? HEALTH_CONFIG.healthy;

  return (
    <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-2.5 sm:p-3 text-white shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        {/* Left Side: Live indicator + Infographic Stat Pills */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Live pulsing radar */}
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold backdrop-blur-xs">
            <span className="relative flex h-2 w-2">
              {!prefersReducedMotion && (
                <motion.span
                  className="absolute inline-flex h-full w-full rounded-full bg-emerald-400"
                  animate={{ scale: [1, 2.2], opacity: [0.8, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
                />
              )}
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="tracking-wide">LIVE</span>
          </div>

          {/* Stat Pill: Active Connections */}
          <Link
            to="/integrations"
            className="group flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 text-xs transition-colors"
            title="View integrations"
          >
            <Link2 className="h-3.5 w-3.5 text-slate-400 group-hover:text-white transition-colors" />
            <span className="font-bold tabular-nums text-white">{stats?.activeConnections ?? 0}</span>
            <span className="text-slate-300 group-hover:text-white transition-colors">connected</span>
            <ArrowUpRight className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          {/* Stat Pill: Messages this hour */}
          <Link
            to="/conversations"
            className="group flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 text-xs transition-colors"
            title="View conversations"
          >
            <MessageSquare className="h-3.5 w-3.5 text-slate-400 group-hover:text-white transition-colors" />
            <span className="font-bold tabular-nums text-white">{stats?.messagesLastHour ?? 0}</span>
            <span className="text-slate-300 group-hover:text-white transition-colors">messages/hr</span>
            <ArrowUpRight className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          {/* Stat Pill: Pending Leads */}
          <Link
            to="/leads"
            className="group flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 text-xs transition-colors"
            title="View leads"
          >
            <Users className="h-3.5 w-3.5 text-slate-400 group-hover:text-white transition-colors" />
            <span className="font-bold tabular-nums text-emerald-400">{stats?.pendingLeads ?? 0}</span>
            <span className="text-slate-300 group-hover:text-white transition-colors">pending leads</span>
            <ArrowUpRight className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>

        {/* Right Side: Health Status Badge */}
        <div
          className={cn(
            "flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium backdrop-blur-xs",
            healthCfg.badgeClass
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", healthCfg.dotClass)} />
          <Activity className="h-3 w-3" />
          <span>System {healthCfg.label}</span>
        </div>
      </div>
    </Card>
  );
}
